import { authenticatedFetch } from '../auth/authClient';
import { errorMessage, type MyProfile, parseProfile, type WeekStartsOn } from './profileModel';

/** The caller's own profile, preferences and photo: /api/me/profile, /preferences, /photo. */

async function payload(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return undefined;
  }
}

async function profileFrom(response: Response, fallback: string): Promise<MyProfile> {
  const body = await payload(response);
  if (!response.ok) throw new Error(errorMessage(body, fallback));
  return parseProfile(body);
}

export async function fetchMyProfile(): Promise<MyProfile> {
  return profileFrom(await authenticatedFetch('/api/me/profile'), 'Your profile could not be loaded.');
}

export async function saveMyProfile(fields: {
  displayName: string;
  title: string | null;
  about: string | null;
}): Promise<MyProfile> {
  return profileFrom(
    await authenticatedFetch('/api/me/profile', {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(fields),
    }),
    'Your profile was not saved.',
  );
}

export async function saveMyWeekStart(weekStartsOn: WeekStartsOn): Promise<MyProfile> {
  return profileFrom(
    await authenticatedFetch('/api/me/preferences', {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ weekStartsOn }),
    }),
    'Week start was not saved.',
  );
}

/** Saves one app's tab order; other apps' orders are kept. */
export async function saveMyTabOrder(app: string, tabs: readonly string[]): Promise<MyProfile> {
  return profileFrom(
    await authenticatedFetch('/api/me/preferences', {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ tabOrder: { app, tabs } }),
    }),
    'Your tab order was not saved.',
  );
}

/** The caller's photo as a blob, or null when there is none. */
export async function fetchMyPhoto(): Promise<Blob | null> {
  const response = await authenticatedFetch('/api/me/photo');
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(errorMessage(await payload(response), 'Your photo could not be loaded.'));
  return response.blob();
}

export async function uploadMyPhoto(file: Blob): Promise<void> {
  const response = await authenticatedFetch('/api/me/photo', {
    method: 'PUT',
    headers: { 'content-type': file.type },
    body: file,
  });
  if (!response.ok) throw new Error(errorMessage(await payload(response), 'Your photo was not saved.'));
}

export async function removeMyPhoto(): Promise<void> {
  const response = await authenticatedFetch('/api/me/photo', { method: 'DELETE' });
  if (!response.ok) throw new Error(errorMessage(await payload(response), 'Your photo was not removed.'));
}
